import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  INCLUDED_FULL,
  WHO_FOR,
  WHO_NOT_FOR,
  DISCLAIMER_TEXT,
  REFUND_TEXT,
  ACCESS_TIMING_TEXT,
} from "@/data/content";
import QuestionCredibilityBlock from "@/components/QuestionCredibilityBlock";
import { ProductPreview } from "@/components/ProductPreview";

const THUMBS = [
  { screen: "dashboard", label: "Dashboard" },
  { screen: "question", label: "State question" },
  { screen: "explanation", label: "Explanation" },
];

const Thumbnails = () => (
  <>
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {THUMBS.map((t) => (
        <Link
          key={t.label}
          to="/preview"
          className="group overflow-hidden rounded-xl border border-[color:rgba(10,27,51,0.14)] bg-[#e9ecf1] p-2 transition-shadow hover:shadow-[0_10px_30px_rgba(7,20,39,0.12)]"
        >
          <ProductPreview screen={t.screen} compact />
          <span className="mt-2 block text-center text-[11px] font-medium text-[color:rgba(11,18,32,0.6)]">
            {t.label}
          </span>
        </Link>
      ))}
    </div>
    <Link
      to="/preview"
      className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F3D8A] hover:underline"
    >
      View all sample screens <ArrowRight className="h-4 w-4" />
    </Link>
  </>
);

const IncludedList = () => (
  <div className="mt-10">
    <h2 className="font-[var(--font-heading)] text-2xl font-semibold text-[var(--ibp-ink)]">
      What’s included
    </h2>
    <ul className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
      {INCLUDED_FULL.map((item) => (
        <li
          key={item}
          className="flex items-start gap-3 rounded-xl border border-[color:rgba(10,27,51,0.10)] bg-white p-3.5 text-sm text-[var(--ibp-ink)]"
        >
          <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[color:rgba(31,138,91,0.12)] text-[#1F8A5B]">
            <Check className="h-3.5 w-3.5" />
          </span>
          {item}
        </li>
      ))}
    </ul>
  </div>
);

const AudienceCard = ({ title, items, negative }) => (
  <div className="rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-6">
    <h3 className="font-[var(--font-heading)] text-lg font-semibold text-[var(--ibp-ink)]">
      {title}
    </h3>
    <ul className="mt-4 space-y-3 text-sm text-[color:rgba(11,18,32,0.78)]">
      {items.map((t) => (
        <li key={t} className="flex gap-3">
          {negative ? (
            <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#B42318]" />
          ) : (
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#1F8A5B]" />
          )}
          <span>{t}</span>
        </li>
      ))}
    </ul>
  </div>
);

const DetailsAccordion = () => (
  <div className="mt-10">
    <Accordion type="single" collapsible className="w-full">
      <AccordionItem value="delivery" className="mb-3 rounded-2xl border border-[color:rgba(10,27,51,0.14)] bg-white px-2">
        <AccordionTrigger className="px-4 text-left text-sm font-semibold text-[var(--ibp-ink)] hover:no-underline">
          How digital delivery works
        </AccordionTrigger>
        <AccordionContent className="px-4 text-sm leading-6 text-[color:rgba(11,18,32,0.72)]">
          Checkout is handled securely by our payment provider. After your purchase completes,
          full lifetime access — yours until you pass — connects to the email used at checkout.{" "}
          {ACCESS_TIMING_TEXT} Buyers also receive free updates as
          the question bank expands.
        </AccordionContent>
      </AccordionItem>
      <AccordionItem value="refund" className="mb-3 rounded-2xl border border-[color:rgba(10,27,51,0.14)] bg-white px-2">
        <AccordionTrigger className="px-4 text-left text-sm font-semibold text-[var(--ibp-ink)] hover:no-underline">
          Refund policy summary
        </AccordionTrigger>
        <AccordionContent className="px-4 text-sm leading-6 text-[color:rgba(11,18,32,0.72)]">
          {REFUND_TEXT}{" "}
          <Link to="/refund" className="font-semibold text-[#0F3D8A] hover:underline">
            Read the full refund policy.
          </Link>
        </AccordionContent>
      </AccordionItem>
      <AccordionItem value="disclaimer" className="rounded-2xl border border-[color:rgba(10,27,51,0.14)] bg-white px-2">
        <AccordionTrigger className="px-4 text-left text-sm font-semibold text-[var(--ibp-ink)] hover:no-underline">
          Disclaimer
        </AccordionTrigger>
        <AccordionContent className="px-4 text-sm leading-6 text-[color:rgba(11,18,32,0.72)]">
          {DISCLAIMER_TEXT}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  </div>
);

export const ProductDetails = () => (
  <div>
    <Thumbnails />
    <IncludedList />
    <QuestionCredibilityBlock className="mt-10" />
    <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2">
      <AudienceCard title="Who it’s for" items={WHO_FOR} />
      <AudienceCard title="Who it’s not for" items={WHO_NOT_FOR} negative />
    </div>
    <DetailsAccordion />
  </div>
);

export default ProductDetails;
